import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box, Paper, Typography, Button, IconButton, Drawer,
    TextField, Divider, Chip, Alert, Snackbar, Tooltip,
    ToggleButton, ToggleButtonGroup, Stack,
    MenuItem, Slider, CircularProgress,
} from '@mui/material';
import {
    ZoomIn as ZoomInIcon,
    ZoomOut as ZoomOutIcon,
    RestartAlt as RestartAltIcon,
    Save as SaveIcon,
    Download as DownloadIcon,
    Upload as UploadIcon,
    Delete as DeleteIcon,
    Clear as ClearIcon,
    GridOn as GridOnIcon,
    GridOff as GridOffIcon,
    SelectAll as SelectAllIcon,
    PanTool as PanToolIcon,
    Close as CloseIcon,
    ContentCopy as CopyIcon,
    Undo as UndoIcon,
    Redo as RedoIcon,
    ConstructionOutlined,
} from '@mui/icons-material';
import { diagram_meter_data } from "../api/DynamicDiagramapi";
import { getUserDetails } from "../api/user";
import { getPlantList } from "../api/plant";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

// ==================== CONSTANTS ====================
const COMPONENT_TYPES = {
    Meter: {
        id: 'Meter', name: 'Energy Meter', color: '#22863a',
        defaultLabel: '0.00', width: 60, height: 80,
        minWidth: 36, minHeight: 50, maxWidth: 160, maxHeight: 220,
    },
    Gateway: {
        id: 'Gateway', name: 'DPM-C530E', color: '#1d4ed8',
        defaultLabel: 'Gateway', width: 60, height: 80,
        minWidth: 36, minHeight: 50, maxWidth: 160, maxHeight: 220,
    },
    Power_Button: {
        id: 'Power_Button', name: 'Circuit Breaker', color: '#6b7280',
        defaultLabel: '63A', width: 40, height: 28,
        minWidth: 24, minHeight: 18, maxWidth: 120, maxHeight: 80,
    },
    Gateway2: {
        id: 'Gateway2', name: 'Transformer', color: '#6b7280',
        defaultLabel: '11kV/400V', width: 52, height: 52,
        minWidth: 34, minHeight: 34, maxWidth: 160, maxHeight: 160,
    },
    CIRCUIT_BREAKER: {
        id: 'CIRCUIT_BREAKER', name: 'Disconnect', color: '#6b7280',
        defaultLabel: 'SW', width: 40, height: 40,
        minWidth: 24, minHeight: 24, maxWidth: 120, maxHeight: 120,
    },
    Load: {
        id: 'Load', name: 'Load / Panel', color: '#374151',
        defaultLabel: 'Load', width: 100, height: 34,
        minWidth: 60, minHeight: 24, maxWidth: 260, maxHeight: 80,
    },
    BusBar: {
        id: 'BusBar', name: 'Bus Bar', color: '#e07820',
        defaultLabel: 'Bus 1', width: 200, height: 12,
        minWidth: 60, minHeight: 6, maxWidth: 800, maxHeight: 20,
    },
    TextLabel: {
        id: 'TextLabel', name: 'Text Label', color: '#374151',
        defaultLabel: 'Label', width: 100, height: 28,
        minWidth: 40, minHeight: 18, maxWidth: 400, maxHeight: 200,
    },
};

const getAnchorPoints = (element) => {
    const { x, y, width, height } = element;
    const cx = x + width / 2;
    const cy = y + height / 2;
    return [
        { x: cx, y: y, name: 'top' },
        { x: x + width, y: cy, name: 'right' },
        { x: cx, y: y + height, name: 'bottom' },
        { x: x, y: cy, name: 'left' },
    ];
};

const SNAP_RADIUS = 14;

const findNearestAnchor = (point, elements) => {
    let nearest = null;
    let minDist = SNAP_RADIUS;
    for (const el of elements) {
        for (const anchor of getAnchorPoints(el)) {
            const d = Math.hypot(point.x - anchor.x, point.y - anchor.y);
            if (d < minDist) { minDist = d; nearest = { ...anchor, elementId: el.id }; }
        }
    }
    return nearest;
};

// Only allow straight horizontal/vertical lines (SLD standard)
const snapLineOrthogonal = (startX, startY, endX, endY) => {
    const dx = Math.abs(endX - startX);
    const dy = Math.abs(endY - startY);
    if (dx >= dy) {
        return { x: endX, y: startY, axis: 'h' };
    } else {
        return { x: startX, y: endY, axis: 'v' };
    }
};

const ALIGN_THRESHOLD = 8;

const computeSnap = (draggingEl, rawX, rawY, allElements) => {
    const others = allElements.filter(e => e.id !== draggingEl.id);
    const w = draggingEl.width;
    const h = draggingEl.height;
    const dragEdges = {
        left: rawX, right: rawX + w, centerX: rawX + w / 2,
        top: rawY, bottom: rawY + h, centerY: rawY + h / 2,
    };
    let snapX = null, snapY = null;
    const hGuides = [], vGuides = [];
    for (const other of others) {
        const oLeft = other.x, oRight = other.x + other.width, oCX = other.x + other.width / 2;
        const oTop = other.y, oBottom = other.y + other.height, oCY = other.y + other.height / 2;
        const xCandidates = [
            { dragEdge: dragEdges.left, otherVal: oLeft, offset: 0 },
            { dragEdge: dragEdges.left, otherVal: oRight, offset: 0 },
            { dragEdge: dragEdges.right, otherVal: oLeft, offset: -w },
            { dragEdge: dragEdges.right, otherVal: oRight, offset: -w },
            { dragEdge: dragEdges.centerX, otherVal: oCX, offset: -w / 2 },
        ];
        for (const c of xCandidates) {
            if (Math.abs(c.dragEdge - c.otherVal) < ALIGN_THRESHOLD) {
                const snappedX = c.otherVal + c.offset;
                if (snapX === null || Math.abs(c.dragEdge - c.otherVal) < Math.abs(snapX - rawX)) {
                    snapX = snappedX;
                    vGuides.push({ x: c.otherVal, y1: Math.min(rawY, oTop) - 20, y2: Math.max(rawY + h, oBottom) + 20 });
                }
            }
        }
        const yCandidates = [
            { dragEdge: dragEdges.top, otherVal: oTop, offset: 0 },
            { dragEdge: dragEdges.top, otherVal: oBottom, offset: 0 },
            { dragEdge: dragEdges.bottom, otherVal: oTop, offset: -h },
            { dragEdge: dragEdges.bottom, otherVal: oBottom, offset: -h },
            { dragEdge: dragEdges.centerY, otherVal: oCY, offset: -h / 2 },
        ];
        for (const c of yCandidates) {
            if (Math.abs(c.dragEdge - c.otherVal) < ALIGN_THRESHOLD) {
                const snappedY = c.otherVal + c.offset;
                if (snapY === null || Math.abs(c.dragEdge - c.otherVal) < Math.abs(snapY - rawY)) {
                    snapY = snappedY;
                    hGuides.push({ y: c.otherVal, x1: Math.min(rawX, oLeft) - 20, x2: Math.max(rawX + w, oRight) + 20 });
                }
            }
        }
    }
    return {
        x: snapX !== null ? snapX : rawX,
        y: snapY !== null ? snapY : rawY,
        guides: { h: hGuides, v: vGuides },
    };
};

// ==================== SLD SYMBOL RENDERERS ====================

// Energy Meter — Schneider PM style green device
const MeterSymbol = ({ el, isSelected, mode, onMouseDown, onResizeStart, renderResizeHandles }) => {
    const w = el.width;
    const h = el.height;
    const screenH = h * 0.32;
    const screenY = h * 0.08;
    const arrowY1 = h * 0.7;
    const arrowY2 = h * 0.82;

    return (
        <g transform={`translate(${el.x},${el.y})`}>
            {/* Shadow */}
            <rect width={w} height={h} rx={3} fill="rgba(0,0,0,0.18)" transform="translate(1.5,2)" style={{ pointerEvents: 'none' }} />

            {/* Main body */}
            <rect width={w} height={h} rx={3}
                fill="#1e4d20" stroke={isSelected ? '#f59e0b' : '#134015'} strokeWidth={isSelected ? 1.5 : 1.2}
                onMouseDown={onMouseDown}
                style={{ cursor: mode === 'select' ? 'grab' : 'crosshair', userSelect: 'none' }}
            />

            {/* LCD Screen */}
            <rect x={w * 0.1} y={screenY} width={w * 0.8} height={screenH} rx={1.5}
                fill="#0f2e11" style={{ pointerEvents: 'none' }} />
            <text x={w / 2} y={screenY + screenH * 0.62}
                textAnchor="middle" fontSize={Math.max(4, w * 0.18)} fontWeight="600"
                fill="#4ade80" fontFamily="monospace" style={{ pointerEvents: 'none', userSelect: 'none' }}>
                465 kW
            </text>

            {/* Separator line */}
            <line x1={w * 0.08} y1={h * 0.44} x2={w * 0.92} y2={h * 0.44}
                stroke="#134015" strokeWidth={0.75} style={{ pointerEvents: 'none' }} />

            <text x={w / 2} y={screenY + screenH * 1.5}
                textAnchor="middle" fontSize={Math.max(3, w * 0.09)}
                fill="#86efac" fontFamily="monospace" style={{ pointerEvents: 'none', userSelect: 'none' }}>
                SCHNEIDER
            </text>

            {/* Panel buttons */}
            <rect x={w * 0.12} y={h * 0.62} width={w * 0.36} height={h * 0.1} rx={1} fill="#134015" style={{ pointerEvents: 'none' }} />
            <rect x={w * 0.52} y={h * 0.62} width={w * 0.36} height={h * 0.1} rx={1} fill="#134015" style={{ pointerEvents: 'none' }} />

            {/* Red flow arrows */}
            <polygon
                points={`${w * 0.30},${h * 0.78} ${w * 0.18},${h * 0.86} ${w * 0.42},${h * 0.86}`}
                fill="#ef4444" style={{ pointerEvents: 'none' }} />
            <polygon
                points={`${w * 0.70},${h * 0.86} ${w * 0.58},${h * 0.78} ${w * 0.82},${h * 0.78}`}
                fill="#ef4444" style={{ pointerEvents: 'none' }} />

            {/* Bottom terminal line */}
            <line x1={w * 0.1} y1={h * 0.92} x2={w * 0.9} y2={h * 0.92}
                stroke="#134015" strokeWidth={0.75} style={{ pointerEvents: 'none' }} />

            {/* Live indicator dot */}
            {el.assignedMeter && (
                <circle cx={w - 5} cy={5} r={3} fill="#22c55e" style={{ pointerEvents: 'none' }}>
                    <animate attributeName="opacity" values="1;0.35;1" dur="2s" repeatCount="indefinite" />
                </circle>
            )}

            {/* Readings — rendered to the right of symbol */}
            {/* <text x={w + 10} y={h * 0.22}
                fontSize={10} fontWeight="600" fill="#1f2937"
                fontFamily="'Segoe UI', Arial, sans-serif" style={{ pointerEvents: 'none', userSelect: 'none' }}>
                {el.label}
            </text>
            <text x={w + 52} y={h * 0.22}
                fontSize={10} fill="#6b7280"
                fontFamily="'Segoe UI', Arial, sans-serif" style={{ pointerEvents: 'none', userSelect: 'none' }}>
                kW
            </text>
            <text x={w + 10} y={h * 0.22 + 14}
                fontSize={10} fill="#6b7280"
                fontFamily="'Segoe UI', Arial, sans-serif" style={{ pointerEvents: 'none', userSelect: 'none' }}>
                {el.name !== 'Energy Meter' ? el.name : '—'}
            </text> */}

            {/* Selection dashed outline */}
            {isSelected && (
                <rect x={-4} y={-4} width={w + 8} height={h + 8} rx={5}
                    fill="none" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="5 3"
                    style={{ pointerEvents: 'none' }} />
            )}

            {isSelected && mode === 'select' && renderResizeHandles(el)}
        </g>
    );
};

// Circuit Breaker — box with X
const BreakerSymbol = ({ el, isSelected, mode, onMouseDown, renderResizeHandles }) => {
    const w = el.width;
    const h = el.height;
    return (
        <g transform={`translate(${el.x},${el.y})`}>
            <rect width={w} height={h} rx={2}
                fill="#f8fafc" stroke={isSelected ? '#f59e0b' : '#64748b'} strokeWidth={isSelected ? 1.5 : 1.5}
                onMouseDown={onMouseDown}
                style={{ cursor: mode === 'select' ? 'grab' : 'crosshair', userSelect: 'none' }}
            />
            <line x1={w * 0.18} y1={h * 0.2} x2={w * 0.82} y2={h * 0.8}
                stroke="#475569" strokeWidth={1.3} style={{ pointerEvents: 'none' }} />
            <line x1={w * 0.82} y1={h * 0.2} x2={w * 0.18} y2={h * 0.8}
                stroke="#475569" strokeWidth={1.3} style={{ pointerEvents: 'none' }} />
            {isSelected && (
                <rect x={-3} y={-3} width={w + 6} height={h + 6} rx={3}
                    fill="none" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="4 3"
                    style={{ pointerEvents: 'none' }} />
            )}
            {isSelected && mode === 'select' && renderResizeHandles(el)}
        </g>
    );
};

// Transformer — two coil circles
const TransformerSymbol = ({ el, isSelected, mode, onMouseDown, renderResizeHandles }) => {
    const w = el.width;
    const h = el.height;
    const r = Math.min(w, h) * 0.28;
    const cx = w / 2;
    return (
        <g transform={`translate(${el.x},${el.y})`}>
            <rect width={w} height={h} rx={w / 2} fill="transparent"
                onMouseDown={onMouseDown}
                style={{ cursor: mode === 'select' ? 'grab' : 'crosshair', userSelect: 'none' }}
            />
            <circle cx={cx} cy={h * 0.3} r={r}
                fill="#f8fafc" stroke={isSelected ? '#f59e0b' : '#64748b'} strokeWidth={1.5}
                style={{ pointerEvents: 'none' }} />
            <circle cx={cx} cy={h * 0.7} r={r}
                fill="#f8fafc" stroke={isSelected ? '#f59e0b' : '#64748b'} strokeWidth={1.5}
                style={{ pointerEvents: 'none' }} />
            <line x1={cx - r} y1={h / 2} x2={cx + r} y2={h / 2}
                stroke="#94a3b8" strokeWidth={0.75} strokeDasharray="2 2" style={{ pointerEvents: 'none' }} />
            {isSelected && (
                <rect x={-3} y={-3} width={w + 6} height={h + 6} rx={4}
                    fill="none" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="4 3"
                    style={{ pointerEvents: 'none' }} />
            )}
            {isSelected && mode === 'select' && renderResizeHandles(el)}
        </g>
    );
};

// Disconnect Switch — knife switch open
const DisconnectSymbol = ({ el, isSelected, mode, onMouseDown, renderResizeHandles }) => {
    const w = el.width;
    const h = el.height;
    const r = 4;
    return (
        <g transform={`translate(${el.x},${el.y})`}>
            <rect width={w} height={h} fill="transparent"
                onMouseDown={onMouseDown}
                style={{ cursor: mode === 'select' ? 'grab' : 'crosshair', userSelect: 'none' }}
            />
            {/* top contact */}
            <circle cx={w / 2} cy={h * 0.2} r={r}
                fill="#f8fafc" stroke={isSelected ? '#f59e0b' : '#64748b'} strokeWidth={1.5}
                style={{ pointerEvents: 'none' }} />
            {/* bottom contact */}
            <circle cx={w / 2} cy={h * 0.8} r={r}
                fill="#f8fafc" stroke={isSelected ? '#f59e0b' : '#64748b'} strokeWidth={1.5}
                style={{ pointerEvents: 'none' }} />
            {/* blade angled (open position) */}
            <line x1={w / 2} y1={h * 0.2} x2={w * 0.75} y2={h * 0.65}
                stroke={isSelected ? '#f59e0b' : '#64748b'} strokeWidth={1.5} strokeLinecap="round"
                style={{ pointerEvents: 'none' }} />
            {isSelected && (
                <rect x={-3} y={-3} width={w + 6} height={h + 6} rx={4}
                    fill="none" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="4 3"
                    style={{ pointerEvents: 'none' }} />
            )}
            {isSelected && mode === 'select' && renderResizeHandles(el)}
        </g>
    );
};

// Gateway — rectangle with wifi arcs
const GatewaySymbol = ({ el, isSelected, mode, onMouseDown, renderResizeHandles }) => {
    const w = el.width;
    const h = el.height;
    const cx = w / 2;
    const cy = h / 2;
    return (
        <g transform={`translate(${el.x},${el.y})`}>
            <rect width={w} height={h} rx={3}
                fill="#f8fafc" stroke={isSelected ? '#f59e0b' : '#1d4ed8'} strokeWidth={isSelected ? 1.5 : 1.5}
                onMouseDown={onMouseDown}
                style={{ cursor: mode === 'select' ? 'grab' : 'crosshair', userSelect: 'none' }}
            />
            <path d={`M ${cx - 14} ${cy - 2} Q ${cx} ${cy - 12} ${cx + 14} ${cy - 2}`}
                fill="none" stroke="#1d4ed8" strokeWidth={1.3} strokeLinecap="round"
                style={{ pointerEvents: 'none' }} />
            <path d={`M ${cx - 8} ${cy + 4} Q ${cx} ${cy - 2} ${cx + 8} ${cy + 4}`}
                fill="none" stroke="#1d4ed8" strokeWidth={1.3} strokeLinecap="round"
                style={{ pointerEvents: 'none' }} />
            <circle cx={cx} cy={cy + 9} r={2.5} fill="#1d4ed8" style={{ pointerEvents: 'none' }} />
            {isSelected && (
                <rect x={-3} y={-3} width={w + 6} height={h + 6} rx={5}
                    fill="none" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="4 3"
                    style={{ pointerEvents: 'none' }} />
            )}
            {isSelected && mode === 'select' && renderResizeHandles(el)}
        </g>
    );
};

// Load / Panel box
const LoadSymbol = ({ el, isSelected, mode, onMouseDown, renderResizeHandles }) => {
    const w = el.width;
    const h = el.height;
    return (
        <g transform={`translate(${el.x},${el.y})`}>
            <rect width={w} height={h} rx={2}
                fill="white" stroke={isSelected ? '#f59e0b' : '#94a3b8'} strokeWidth={isSelected ? 1.5 : 1}
                onMouseDown={onMouseDown}
                style={{ cursor: mode === 'select' ? 'grab' : 'crosshair', userSelect: 'none' }}
            />
            <text x={w / 2} y={h / 2 + 4}
                textAnchor="middle" fontSize={11} fill="#374151"
                fontFamily="'Segoe UI', Arial, sans-serif" style={{ pointerEvents: 'none', userSelect: 'none' }}>
                {el.label}
            </text>
            {isSelected && (
                <rect x={-3} y={-3} width={w + 6} height={h + 6} rx={4}
                    fill="none" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="4 3"
                    style={{ pointerEvents: 'none' }} />
            )}
            {isSelected && mode === 'select' && renderResizeHandles(el)}
        </g>
    );
};

// Bus Bar — thick colored horizontal bar
const BusBarSymbol = ({ el, isSelected, mode, onMouseDown, renderResizeHandles }) => {
    const w = el.width;
    const h = el.height;
    const mid = h / 2;
    return (
        <g transform={`translate(${el.x},${el.y})`}>
            {/* invisible hit area */}
            <rect width={w} height={h} fill="transparent"
                onMouseDown={onMouseDown}
                style={{ cursor: mode === 'select' ? 'grab' : 'crosshair', userSelect: 'none' }}
            />
            {/* main bus bar */}
            <line x1={0} y1={mid} x2={w} y2={mid}
                stroke={el.color || '#e07820'} strokeWidth={Math.max(h * 0.55, 4)} strokeLinecap="round"
                style={{ pointerEvents: 'none' }} />
            {/* label */}
            <text x={-6} y={mid + 4}
                textAnchor="end" fontSize={9} fill={el.color || '#e07820'}
                fontFamily="'Segoe UI', Arial, sans-serif" fontWeight="600"
                style={{ pointerEvents: 'none', userSelect: 'none' }}>
                {el.label}
            </text>
            {isSelected && (
                <rect x={-3} y={-4} width={w + 6} height={h + 8} rx={3}
                    fill="none" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="4 3"
                    style={{ pointerEvents: 'none' }} />
            )}
            {isSelected && mode === 'select' && renderResizeHandles(el)}
        </g>
    );
};

// Text Label — editable free text on canvas
const TextLabelSymbol = ({ el, isSelected, mode, onMouseDown, renderResizeHandles, onDoubleClick }) => {
    const w = el.width;
    const h = el.height;
    const fontSize = el.fontSize || 13;
    const fontWeight = el.bold ? '700' : '400';
    const fontStyle = el.italic ? 'italic' : 'normal';
    const textColor = el.textColor || '#1f2937';
    const hasBorder = el.showBorder;
    return (
        <g transform={`translate(${el.x},${el.y})`}>
            {/* Transparent hit area always */}
            <rect width={w} height={h} fill={hasBorder ? 'rgba(255,255,255,0.8)' : 'transparent'}
                stroke={isSelected ? '#f59e0b' : hasBorder ? '#94a3b8' : 'transparent'}
                strokeWidth={isSelected ? 1.5 : 1}
                rx={2}
                onMouseDown={onMouseDown}
                onDoubleClick={onDoubleClick}
                style={{ cursor: mode === 'select' ? 'grab' : 'crosshair', userSelect: 'none' }}
            />
            {/* If selected but no border, show faint background */}
            {isSelected && !hasBorder && (
                <rect width={w} height={h} fill="rgba(245,158,11,0.05)" rx={2}
                    style={{ pointerEvents: 'none' }} />
            )}
            {/* The text — wraps within foreignObject for multi-line */}
            <foreignObject x={4} y={2} width={w - 8} height={h - 4} style={{ pointerEvents: 'none', overflow: 'visible' }}>
                <div xmlns="http://www.w3.org/1999/xhtml" style={{
                    fontSize: `${fontSize}px`,
                    fontWeight,
                    fontStyle,
                    color: textColor,
                    fontFamily: "'Segoe UI', Arial, sans-serif",
                    lineHeight: 1.4,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    userSelect: 'none',
                    pointerEvents: 'none',
                    width: '100%',
                    height: '100%',
                    overflow: 'hidden',
                }}>
                    {el.label}
                </div>
            </foreignObject>
            {/* Selection dashed outline */}
            {isSelected && (
                <rect x={-3} y={-3} width={w + 6} height={h + 6} rx={4}
                    fill="none" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="5 3"
                    style={{ pointerEvents: 'none' }} />
            )}
            {isSelected && mode === 'select' && renderResizeHandles(el)}
        </g>
    );
};

// ==================== MAIN COMPONENT ====================
function DiagramEditor() {
    const [elements, setElements] = useState([]);
    const navigate = useNavigate();
    const [freeLines, setFreeLines] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [mode, setMode] = useState('select');
    const [zoom, setZoom] = useState(1);
    const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
    const [showGrid, setShowGrid] = useState(true);
    const [showProperties, setShowProperties] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [history, setHistory] = useState([{ elements: [], freeLines: [] }]);
    const [historyIndex, setHistoryIndex] = useState(0);
    const [lineColor, setLineColor] = useState('#374151');
    const [lineWidth, setLineWidth] = useState(1.5);
    const [drawingLine, setDrawingLine] = useState(null);
    const [snappedEnd, setSnappedEnd] = useState(null);
    const [dragging, setDragging] = useState(null);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [resizing, setResizing] = useState(null);
    const [resizeHandle, setResizeHandle] = useState(null);
    const [resizeStart, setResizeStart] = useState(null);
    const [panning, setPanning] = useState(false);
    const [panStart, setPanStart] = useState({ x: 0, y: 0 });
    const [panOffsetStart, setPanOffsetStart] = useState({ x: 0, y: 0 });
    const [snapGuides, setSnapGuides] = useState({ h: [], v: [] });
    const [editingTextId, setEditingTextId] = useState(null);
    const [editingTextValue, setEditingTextValue] = useState('');
    const [diagramName, setDiagramName] = useState("Untitled Diagram");
    const [diagramId, setDiagramId] = useState(null);
    const diagramIdRef = useRef(null);
    const [apiData, setApiData] = useState([]);
    const [meterData, setMeterData] = useState(null);
    const [loadingData, setLoadingData] = useState(false);
    const [userDetail, setUserDetail] = useState(null);
    const [loadingUser, setLoadingUser] = useState(true);
    const [assignedMeters, setAssignedMeters] = useState({});
    const [clientList, setClientList] = useState([]);
    const [selectedClientId, setSelectedClientId] = useState(null);
    const [loadingClients, setLoadingClients] = useState(false);
    const [diagramOwnerClientId, setDiagramOwnerClientId] = useState(null);
    console.log(diagramOwnerClientId)
    const [meterDataSourceClientId, setMeterDataSourceClientId] = useState(null);
    const meterDataSourceClientIdRef = useRef(null);


    useEffect(() => { meterDataSourceClientIdRef.current = meterDataSourceClientId; }, [meterDataSourceClientId]);

    const selectedClientIdRef = useRef(null);
    const canvasRef = useRef(null);
    const elementsRef = useRef([]);

    const API_BASE = "https://api.ai-ecos.com";
    const token = () => localStorage.getItem("token");

    useEffect(() => { elementsRef.current = elements; }, [elements]);
    useEffect(() => { selectedClientIdRef.current = selectedClientId; }, [selectedClientId]);

    // ==================== USER & PLANT LOAD ====================
    useEffect(() => {
        const fetchUserDetails = async () => {
            try {
                setLoadingUser(true);
                const userData = await getUserDetails();
                setUserDetail(userData);
                if (userData?.client_id) {
                    setDiagramOwnerClientId(userData.client_id);
                    setSelectedClientId(userData.client_id);
                    selectedClientIdRef.current = userData.client_id;
                    localStorage.setItem("client_id", String(userData.client_id));
                    if (!meterDataSourceClientId) {
                        setMeterDataSourceClientId(userData.client_id);
                        meterDataSourceClientIdRef.current = userData.client_id;
                    }
                }
            } catch (error) {
                console.error("Error fetching user details:", error);
                showSnackbar("Failed to load user configuration", "error");
            } finally {
                setLoadingUser(false);
            }
        };
        fetchUserDetails();
    }, []);

    useEffect(() => {
        const fetchPlantList = async () => {
            setLoadingClients(true);
            try {
                const plantList = await getPlantList();
                if (Array.isArray(plantList.data)) {
                    const filteredClients = plantList.data.filter(client =>
                        client.access_level === 1 &&
                        (client.user_type === 0 || client.user_type === 1)
                    );
                    setClientList(filteredClients);
                }
            } catch (error) {
                console.error("Error fetching client list:", error);
            } finally {
                setLoadingClients(false);
            }
        };
        fetchPlantList();
    }, []);

    const [diagramLoaded, setDiagramLoaded] = useState(false);

    useEffect(() => {
        if (!userDetail?.client_id || diagramLoaded) return;

        const pendingName = localStorage.getItem("pendingDiagramName");
        const existingId = localStorage.getItem("lastDiagramId");
        const ownerClientId = localStorage.getItem("diagram_owner_client_id");

        if (existingId && ownerClientId) {
            setDiagramLoaded(true);
            diagramIdRef.current = existingId;
            setDiagramId(existingId);
            setDiagramOwnerClientId(ownerClientId);
            setSelectedClientId(ownerClientId);
            loadDiagramFromDB(existingId, ownerClientId);
        } else if (pendingName) {
            setDiagramLoaded(true);
            setDiagramName(pendingName);
            localStorage.removeItem("pendingDiagramName");
            setElements([]);
            setFreeLines([]);
            setZoom(1);
            setPanOffset({ x: 0, y: 0 });
        }
    }, [userDetail?.client_id, diagramLoaded]);

    // ==================== METER DATA FETCH ====================
    const fetchMeterData = async (clientIdOverride = null) => {
        const clientId = clientIdOverride !== null ? clientIdOverride : meterDataSourceClientIdRef.current;
        if (!clientId) { showSnackbar("No meter data source selected", "warning"); return []; }
        setLoadingData(true);
        try {
            const response = await diagram_meter_data(parseInt(clientId));
            let meterArray = [];
            if (Array.isArray(response)) meterArray = response;
            else if (response?.data && Array.isArray(response.data)) meterArray = response.data;
            setApiData(meterArray);
            return meterArray;
        } catch (error) {
            console.error("Error fetching meter data:", error);
            showSnackbar("Failed to load meter data", "error");
            return [];
        } finally {
            setLoadingData(false);
        }
    };

    const refreshLiveMeterData = async (loadedElements, clientId) => {
        if (!clientId) return;
        try {
            const response = await diagram_meter_data(parseInt(clientId));
            let meterArray = [];
            if (Array.isArray(response)) meterArray = response;
            else if (response?.data && Array.isArray(response.data)) meterArray = response.data;
            if (parseInt(clientId) === parseInt(meterDataSourceClientIdRef.current)) setApiData(meterArray);
            setElements(prev => prev.map(el => {
                if (el.type !== 'Meter' || !el.assignedMeter) return el;
                const live = meterArray.find(m => m.serial_number === el.assignedMeter);
                if (!live || !live.data?.[0]) return el;
                const kw = live.data[0].total_active_power_kw;
                return {
                    ...el,
                    label: kw !== null && kw !== undefined ? `${kw}` : el.label,
                    name: live.description || live.usage || el.name,
                };
            }));
        } catch (err) { console.error("Live data refresh failed:", err); }
    };

    // ==================== HELPERS ====================
    const updateDiagramId = (id) => {
        const strId = String(id);
        setDiagramId(strId);
        diagramIdRef.current = strId;
        localStorage.setItem("lastDiagramId", strId);
    };

    const loadDiagramFromDB = async (id, ownerId = null) => {
        const ownerClientId = ownerId || diagramOwnerClientId;
        if (!ownerClientId) {
            console.error("No owner client ID found");
            showSnackbar("Cannot load diagram: missing owner information", "error");
            return;
        }
        try {
            const token = sessionStorage.getItem('accessToken');
            const res = await fetch(`${API_BASE}/api/diagram/?client_id=${ownerClientId}&diagram_id=${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json();
            if (data.success && data.data) {
                const d = data.data;
                setDiagramName(d.name || "Untitled");
                setElements(d.elements || []);
                setFreeLines(d.freeLines || []);
                setZoom(d.zoom || 1);
                setPanOffset(d.panOffset || { x: 0, y: 0 });
                const meterDataSourceId = d.meter_data_client_id || d.client_id || ownerClientId;
                setMeterDataSourceClientId(meterDataSourceId);
                meterDataSourceClientIdRef.current = meterDataSourceId;
                setSelectedClientId(meterDataSourceId);
                selectedClientIdRef.current = meterDataSourceId;
                const restoredAssignedMeters = {};
                (d.elements || []).forEach(el => {
                    if (el.type === 'Meter' && el.assignedMeter) restoredAssignedMeters[el.assignedMeter] = el.id;
                });
                setAssignedMeters(restoredAssignedMeters);
                await refreshLiveMeterData(d.elements || [], meterDataSourceId);
                showSnackbar(`Loaded diagram: ${d.name}`, "success");
            } else {
                showSnackbar("Diagram not found", "error");
            }
        } catch (err) {
            console.error("Load error:", err);
            showSnackbar("Failed to load diagram: " + err.message, "error");
        }
    };

    // ==================== COORDINATE TRANSFORM ====================
    const screenToCanvas = useCallback((clientX, clientY) => {
        const rect = canvasRef.current?.getBoundingClientRect();
        if (!rect) return { x: 0, y: 0 };
        return {
            x: (clientX - rect.left - panOffset.x) / zoom,
            y: (clientY - rect.top - panOffset.y) / zoom,
        };
    }, [zoom, panOffset]);

    // ==================== HISTORY ====================
    const pushHistory = useCallback((els, lines) => {
        setHistory(prev => {
            const next = prev.slice(0, historyIndex + 1);
            next.push({ elements: JSON.parse(JSON.stringify(els)), freeLines: JSON.parse(JSON.stringify(lines)) });
            if (next.length > 50) next.shift();
            return next;
        });
        setHistoryIndex(prev => Math.min(prev + 1, 49));
    }, [historyIndex]);

    const undo = useCallback(() => {
        if (historyIndex > 0) {
            const state = history[historyIndex - 1];
            setElements(state.elements);
            setFreeLines(state.freeLines);
            setHistoryIndex(i => i - 1);
            showSnackbar('Undo', 'info');
        }
    }, [history, historyIndex]);

    const redo = useCallback(() => {
        if (historyIndex < history.length - 1) {
            const state = history[historyIndex + 1];
            setElements(state.elements);
            setFreeLines(state.freeLines);
            setHistoryIndex(i => i + 1);
            showSnackbar('Redo', 'info');
        }
    }, [history, historyIndex]);

    const showSnackbar = (message, severity = 'success') =>
        setSnackbar({ open: true, message, severity });

    const generateId = () => `e_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;

    // ==================== ELEMENT OPS ====================
    const addElement = (type, x, y) => {
        const comp = COMPONENT_TYPES[type];
        if (!comp) return;
        const el = {
            id: generateId(),
            type,
            name: comp.name,
            color: comp.color,
            label: comp.defaultLabel,
            x: x - comp.width / 2,
            y: y - comp.height / 2,
            width: comp.width,
            height: comp.height,
            minWidth: comp.minWidth,
            minHeight: comp.minHeight,
            maxWidth: comp.maxWidth,
            maxHeight: comp.maxHeight,
            assignedMeter: null,
        };
        const next = [...elements, el];
        setElements(next);
        pushHistory(next, freeLines);
        showSnackbar(`${comp.name} added`);
    };

    const rebuildLines = (newEls, lines, movedId) =>
        lines.map(line => {
            let l = { ...line, points: [...line.points] };
            const el = newEls.find(e => e.id === movedId);
            if (!el) return l;
            if (line.startAnchor?.elementId === movedId) {
                const a = getAnchorPoints(el).find(p => p.name === line.startAnchor.name);
                if (a) { l.startAnchor = { ...a, elementId: movedId }; l.points[0] = { x: a.x, y: a.y }; }
            }
            if (line.endAnchor?.elementId === movedId) {
                const a = getAnchorPoints(el).find(p => p.name === line.endAnchor.name);
                if (a) { l.endAnchor = { ...a, elementId: movedId }; l.points[1] = { x: a.x, y: a.y }; }
            }
            return l;
        });

    const updateElement = (id, updates) => {
        const next = elements.map(el => el.id === id ? { ...el, ...updates } : el);
        const nextLines = rebuildLines(next, freeLines, id);
        setElements(next);
        setFreeLines(nextLines);
        pushHistory(next, nextLines);
    };

    const deleteElement = (id) => {
        const nextLines = freeLines.filter(l => l.startAnchor?.elementId !== id && l.endAnchor?.elementId !== id);
        const next = elements.filter(el => el.id !== id);
        setElements(next); setFreeLines(nextLines);
        setSelectedId(null); setShowProperties(false);
        pushHistory(next, nextLines);
        showSnackbar('Deleted', 'info');
    };

    const duplicateElement = (id) => {
        const el = elements.find(e => e.id === id);
        if (!el) return;
        const dup = { ...el, id: generateId(), x: el.x + 20, y: el.y + 20, label: el.label, assignedMeter: null };
        const next = [...elements, dup];
        setElements(next); setSelectedId(dup.id);
        pushHistory(next, freeLines);
        showSnackbar('Duplicated');
    };

    const deleteLine = (id) => {
        const next = freeLines.filter(l => l.id !== id);
        setFreeLines(next); setSelectedId(null);
        pushHistory(elements, next);
        showSnackbar('Wire deleted', 'info');
    };

    // ==================== SAVE ====================
    const saveToDB = async () => {
        const loginClientId = userDetail?.client_id;
        const ownerClientId = diagramOwnerClientId || loginClientId;

        if (!ownerClientId) {
            showSnackbar("No diagram owner found", "error");
            return;
        }

        try {
            const currentId = diagramIdRef.current;
            const body = {
                client_id: parseInt(loginClientId),     // login user's client ID (required by API)
                owner_client_id: parseInt(ownerClientId), // the actual owner of this diagram
                name: diagramName,
                elements,
                freeLines,
                zoom,
                panOffset,
                version: 1,
                meter_data_client_id: meterDataSourceClientId || ownerClientId,
            };
            if (currentId) body.diagramId = currentId;
            const token = sessionStorage.getItem('accessToken');
            const res = await fetch(`${API_BASE}/api/diagram/`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify(body),
            });
            const data = await res.json();
            if (data.success) {
                if (data.diagram_id) updateDiagramId(data.diagram_id);
                showSnackbar("Diagram saved!");
            } else {
                throw new Error(data.error || "Save failed");
            }
        } catch (err) {
            console.error("Save error:", err);
            showSnackbar("Failed to save: " + err.message, "error");
        }
    };
    // ==================== MOUSE HANDLERS ====================
    const handleCanvasMouseDown = useCallback((e) => {
        if (e.button !== 0) return;
        const pt = screenToCanvas(e.clientX, e.clientY);

        if (mode === 'pan') {
            e.preventDefault();
            setPanning(true);
            setPanStart({ x: e.clientX, y: e.clientY });
            setPanOffsetStart({ ...panOffset });
            return;
        }

        if (mode === 'line') {
            e.preventDefault();
            const anchor = findNearestAnchor(pt, elements);
            setDrawingLine({
                startX: anchor ? anchor.x : pt.x,
                startY: anchor ? anchor.y : pt.y,
                currentX: anchor ? anchor.x : pt.x,
                currentY: anchor ? anchor.y : pt.y,
                startAnchor: anchor || null,
            });
            return;
        }

        if (mode === 'select') {
            const clicked = [...elements].reverse().find(el =>
                pt.x >= el.x && pt.x <= el.x + el.width &&
                pt.y >= el.y && pt.y <= el.y + el.height
            );
            if (!clicked) { setSelectedId(null); setShowProperties(false); }
        }
    }, [mode, panOffset, elements, screenToCanvas]);

    const handleElementMouseDown = useCallback((e, elementId) => {
        if (mode !== 'select') return;
        e.stopPropagation();
        const pt = screenToCanvas(e.clientX, e.clientY);
        const el = elements.find(e => e.id === elementId);
        if (!el) return;
        setSelectedId(elementId);
        setShowProperties(true);
        setDragging(elementId);
        setDragOffset({ x: pt.x - el.x, y: pt.y - el.y });
    }, [mode, elements, screenToCanvas]);

    const handleResizeStart = useCallback((e, elementId, handle) => {
        e.stopPropagation();
        e.preventDefault();
        const el = elements.find(e => e.id === elementId);
        if (!el) return;
        const pt = screenToCanvas(e.clientX, e.clientY);
        setResizing(elementId);
        setResizeHandle(handle);
        setResizeStart({ startX: pt.x, startY: pt.y, width: el.width, height: el.height, x: el.x, y: el.y });
    }, [elements, screenToCanvas]);

    const handleCanvasMouseMove = useCallback((e) => {
        const pt = screenToCanvas(e.clientX, e.clientY);

        if (panning) {
            const dx = e.clientX - panStart.x;
            const dy = e.clientY - panStart.y;
            setPanOffset({ x: panOffsetStart.x + dx, y: panOffsetStart.y + dy });
            return;
        }

        if (drawingLine) {
            const anchor = findNearestAnchor(pt, elements);
            setSnappedEnd(anchor);
            let endX = anchor ? anchor.x : pt.x;
            let endY = anchor ? anchor.y : pt.y;
            if (!anchor) {
                const snapped = snapLineOrthogonal(drawingLine.startX, drawingLine.startY, endX, endY);
                endX = snapped.x;
                endY = snapped.y;
            }
            setDrawingLine(prev => prev ? { ...prev, currentX: endX, currentY: endY } : null);
            return;
        }

        if (resizing && resizeHandle && resizeStart) {
            const el = elements.find(e => e.id === resizing);
            if (!el) return;
            const dx = pt.x - resizeStart.startX;
            const dy = pt.y - resizeStart.startY;
            let { width, height, x, y } = resizeStart;
            const clampW = (w) => Math.min(el.maxWidth, Math.max(el.minWidth, w));
            const clampH = (h) => Math.min(el.maxHeight, Math.max(el.minHeight, h));
            let nw = width, nh = height, nx = x, ny = y;
            switch (resizeHandle) {
                case 'right': nw = clampW(width + dx); break;
                case 'left': nw = clampW(width - dx); nx = x + (width - nw); break;
                case 'bottom': nh = clampH(height + dy); break;
                case 'top': nh = clampH(height - dy); ny = y + (height - nh); break;
                case 'bottom-right': nw = clampW(width + dx); nh = clampH(height + dy); break;
                case 'bottom-left': nw = clampW(width - dx); nx = x + (width - nw); nh = clampH(height + dy); break;
                case 'top-right': nw = clampW(width + dx); nh = clampH(height - dy); ny = y + (height - nh); break;
                case 'top-left': nw = clampW(width - dx); nx = x + (width - nw); nh = clampH(height - dy); ny = y + (height - nh); break;
                default: break;
            }
            const next = elements.map(e => e.id === resizing ? { ...e, x: nx, y: ny, width: nw, height: nh } : e);
            const nextLines = rebuildLines(next, freeLines, resizing);
            setElements(next);
            setFreeLines(nextLines);
            return;
        }

        if (dragging) {
            const rawX = pt.x - dragOffset.x;
            const rawY = pt.y - dragOffset.y;
            const draggingEl = elements.find(el => el.id === dragging);
            const { x: snappedX, y: snappedY, guides } = draggingEl
                ? computeSnap(draggingEl, rawX, rawY, elements)
                : { x: rawX, y: rawY, guides: { h: [], v: [] } };
            setSnapGuides(guides);
            const next = elements.map(el => el.id === dragging ? { ...el, x: snappedX, y: snappedY } : el);
            const nextLines = rebuildLines(next, freeLines, dragging);
            setElements(next);
            setFreeLines(nextLines);
        }
    }, [panning, panStart, panOffsetStart, drawingLine, resizing, resizeHandle, resizeStart,
        dragging, dragOffset, elements, freeLines, screenToCanvas]);

    const handleCanvasMouseUp = useCallback((e) => {
        if (drawingLine) {
            const pt = screenToCanvas(e.clientX, e.clientY);
            const anchor = findNearestAnchor(pt, elements);
            let ex = anchor ? anchor.x : pt.x;
            let ey = anchor ? anchor.y : pt.y;
            if (!anchor) {
                const snapped = snapLineOrthogonal(drawingLine.startX, drawingLine.startY, ex, ey);
                ex = snapped.x;
                ey = snapped.y;
            }
            if (Math.hypot(ex - drawingLine.startX, ey - drawingLine.startY) > 5) {
                const line = {
                    id: generateId(),
                    points: [{ x: drawingLine.startX, y: drawingLine.startY }, { x: ex, y: ey }],
                    startAnchor: drawingLine.startAnchor,
                    endAnchor: anchor || null,
                    color: lineColor, width: lineWidth, style: 'solid',
                };
                const next = [...freeLines, line];
                setFreeLines(next);
                pushHistory(elements, next);
                showSnackbar('Wire added');
            }
            setDrawingLine(null);
            setSnappedEnd(null);
        }
        if (dragging) { pushHistory(elements, freeLines); setDragging(null); setSnapGuides({ h: [], v: [] }); }
        if (resizing) { pushHistory(elements, freeLines); setResizing(null); setResizeHandle(null); setResizeStart(null); }
        if (panning) setPanning(false);
    }, [drawingLine, dragging, resizing, panning, elements, freeLines, lineColor, lineWidth, screenToCanvas, pushHistory]);

    useEffect(() => {
        window.addEventListener('mousemove', handleCanvasMouseMove);
        window.addEventListener('mouseup', handleCanvasMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleCanvasMouseMove);
            window.removeEventListener('mouseup', handleCanvasMouseUp);
        };
    }, [handleCanvasMouseMove, handleCanvasMouseUp]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.target.tagName === 'INPUT') return;
            if (e.ctrlKey && e.key === 'z') { e.preventDefault(); undo(); }
            else if (e.ctrlKey && e.key === 'y') { e.preventDefault(); redo(); }
            else if (e.key === 'Delete' && selectedId) {
                if (elements.find(el => el.id === selectedId)) deleteElement(selectedId);
                else if (freeLines.find(l => l.id === selectedId)) deleteLine(selectedId);
            } else if (e.key === 'Escape' && drawingLine) {
                setDrawingLine(null); setSnappedEnd(null);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [selectedId, elements, freeLines, drawingLine, undo, redo]);

    const handleWheel = useCallback((e) => {
        e.preventDefault();
        const rect = canvasRef.current?.getBoundingClientRect();
        if (!rect) return;
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        const newZoom = Math.min(3, Math.max(0.15, zoom * delta));
        setPanOffset({
            x: mouseX - (mouseX - panOffset.x) * (newZoom / zoom),
            y: mouseY - (mouseY - panOffset.y) * (newZoom / zoom),
        });
        setZoom(newZoom);
    }, [zoom, panOffset]);

    useEffect(() => {
        const el = canvasRef.current;
        if (!el) return;
        el.addEventListener('wheel', handleWheel, { passive: false });
        return () => el.removeEventListener('wheel', handleWheel);
    }, [handleWheel]);

    const handleDrop = (e) => {
        e.preventDefault();
        const type = e.dataTransfer.getData('text/plain');
        const pt = screenToCanvas(e.clientX, e.clientY);
        addElement(type, pt.x, pt.y);
    };

    const clearCanvas = () => {
        if (window.confirm('Clear all elements and wires?')) {
            setElements([]); setFreeLines([]);
            setZoom(1); setPanOffset({ x: 0, y: 0 });
            pushHistory([], []);
            showSnackbar('Canvas cleared');
        }
    };

    const handleZoomIn = () => setZoom(z => Math.min(z + 0.1, 3));
    const handleZoomOut = () => setZoom(z => Math.max(z - 0.1, 0.15));
    const handleZoomReset = () => { setZoom(1); setPanOffset({ x: 0, y: 0 }); };
    const goBackToGallery = () => navigate('/DynamicDiagram');

    if (loadingUser) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#f0f2f5' }}>
                <CircularProgress size={36} />
                <Typography sx={{ ml: 2, color: 'text.secondary' }}>Loading...</Typography>
            </Box>
        );
    }

    // ==================== RESIZE HANDLES ====================
    const HANDLE_SIZE = 8;
    const handleDefs = [
        { pos: 'top-left', cursor: 'nw-resize', rx: -HANDLE_SIZE / 2, ry: -HANDLE_SIZE / 2 },
        { pos: 'top', cursor: 'n-resize', rx: (el) => el.width / 2 - HANDLE_SIZE / 2, ry: -HANDLE_SIZE / 2 },
        { pos: 'top-right', cursor: 'ne-resize', rx: (el) => el.width - HANDLE_SIZE / 2, ry: -HANDLE_SIZE / 2 },
        { pos: 'right', cursor: 'e-resize', rx: (el) => el.width - HANDLE_SIZE / 2, ry: (el) => el.height / 2 - HANDLE_SIZE / 2 },
        { pos: 'bottom-right', cursor: 'se-resize', rx: (el) => el.width - HANDLE_SIZE / 2, ry: (el) => el.height - HANDLE_SIZE / 2 },
        { pos: 'bottom', cursor: 's-resize', rx: (el) => el.width / 2 - HANDLE_SIZE / 2, ry: (el) => el.height - HANDLE_SIZE / 2 },
        { pos: 'bottom-left', cursor: 'sw-resize', rx: -HANDLE_SIZE / 2, ry: (el) => el.height - HANDLE_SIZE / 2 },
        { pos: 'left', cursor: 'w-resize', rx: -HANDLE_SIZE / 2, ry: (el) => el.height / 2 - HANDLE_SIZE / 2 },
    ];

    const renderResizeHandles = (el) =>
        handleDefs.map(h => (
            <rect key={h.pos}
                x={typeof h.rx === 'function' ? h.rx(el) : h.rx}
                y={typeof h.ry === 'function' ? h.ry(el) : h.ry}
                width={HANDLE_SIZE} height={HANDLE_SIZE}
                fill="white" stroke="#f59e0b" strokeWidth={1.5} rx={1}
                style={{ cursor: h.cursor }}
                onMouseDown={(e) => handleResizeStart(e, el.id, h.pos)}
            />
        ));

    // ==================== RENDER ELEMENTS ====================
    const renderElements = () => elements.map(el => {
        const isSelected = selectedId === el.id;
        const commonProps = {
            el, isSelected, mode,
            onMouseDown: (e) => handleElementMouseDown(e, el.id),
            renderResizeHandles,
        };

        switch (el.type) {
            case 'Meter':
                return <MeterSymbol key={el.id} {...commonProps} />;
            case 'Gateway':
                return <GatewaySymbol key={el.id} {...commonProps} />;
            case 'Power_Button':
                return <BreakerSymbol key={el.id} {...commonProps} />;
            case 'Gateway2':
                return <TransformerSymbol key={el.id} {...commonProps} />;
            case 'CIRCUIT_BREAKER':
                return <DisconnectSymbol key={el.id} {...commonProps} />;
            case 'Load':
                return <LoadSymbol key={el.id} {...commonProps} />;
            case 'BusBar':
                return <BusBarSymbol key={el.id} {...commonProps} />;
            case 'TextLabel':
                return <TextLabelSymbol key={el.id} {...commonProps}
                    onDoubleClick={(e) => {
                        e.stopPropagation();
                        setEditingTextId(el.id);
                        setEditingTextValue(el.label);
                    }} />;
            default:
                return <LoadSymbol key={el.id} {...commonProps} />;
        }
    });

    // ==================== RENDER LINES (orthogonal SLD wires) ====================
    const renderLines = () => freeLines.map(line => {
        const isSelected = selectedId === line.id;
        const [s, e] = line.points;
        return (
            <g key={line.id}>
                {/* Invisible fat hit area */}
                <line x1={s.x} y1={s.y} x2={e.x} y2={e.y}
                    stroke="transparent" strokeWidth={12}
                    onClick={(ev) => { ev.stopPropagation(); setSelectedId(line.id); setShowProperties(true); }}
                    style={{ cursor: 'pointer' }} />
                {/* Actual wire */}
                <line x1={s.x} y1={s.y} x2={e.x} y2={e.y}
                    stroke={isSelected ? '#f59e0b' : line.color}
                    strokeWidth={isSelected ? line.width + 1 : line.width}
                    strokeDasharray={line.style === 'dashed' ? '6,4' : undefined}
                    style={{ pointerEvents: 'none' }} />
                {/* Junction dots at anchored endpoints */}
                {line.startAnchor && (
                    <circle cx={s.x} cy={s.y} r={3} fill={isSelected ? '#f59e0b' : line.color} style={{ pointerEvents: 'none' }} />
                )}
                {line.endAnchor && (
                    <circle cx={e.x} cy={e.y} r={3} fill={isSelected ? '#f59e0b' : line.color} style={{ pointerEvents: 'none' }} />
                )}
            </g>
        );
    });

    // ==================== PALETTE ====================
    const SLD_PALETTE_ITEMS = [
        {
            key: 'Meter', label: 'Energy Meter', sub: 'kW/A/V',
            preview: (
                <svg width="28" height="38" viewBox="0 0 28 38" style={{ flexShrink: 0 }}>
                    <rect x="1" y="1" width="26" height="36" rx="2" fill="#1e4d20" stroke="#134015" strokeWidth="1" />
                    <rect x="3" y="3" width="22" height="12" rx="1" fill="#0f2e11" />
                    <text x="14" y="11" textAnchor="middle" fontSize="4.5" fill="#4ade80" fontFamily="monospace" fontWeight="600">PM5350</text>
                    <polygon points="8,28 5,23 11,23" fill="#ef4444" />
                    <polygon points="20,23 17,28 23,28" fill="#ef4444" />
                </svg>
            ),
        },
        {
            key: 'Power_Button', label: 'Circuit Breaker', sub: 'MCB / MCCB',
            preview: (
                <svg width="28" height="22" viewBox="0 0 28 22" style={{ flexShrink: 0 }}>
                    <rect x="1" y="1" width="26" height="20" rx="2" fill="#f8fafc" stroke="#64748b" strokeWidth="1.5" />
                    <line x1="5" y1="4" x2="23" y2="18" stroke="#475569" strokeWidth="1.3" />
                    <line x1="23" y1="4" x2="5" y2="18" stroke="#475569" strokeWidth="1.3" />
                </svg>
            ),
        },
        {
            key: 'Gateway2', label: 'Transformer', sub: '11kV / 400V',
            preview: (
                <svg width="28" height="38" viewBox="0 0 28 38" style={{ flexShrink: 0 }}>
                    <circle cx="14" cy="11" r="9" fill="#f8fafc" stroke="#64748b" strokeWidth="1.5" />
                    <circle cx="14" cy="28" r="9" fill="#f8fafc" stroke="#64748b" strokeWidth="1.5" />
                    <line x1="3" y1="19.5" x2="25" y2="19.5" stroke="#94a3b8" strokeWidth="0.75" strokeDasharray="2 2" />
                </svg>
            ),
        },
        {
            key: 'CIRCUIT_BREAKER', label: 'Disconnect', sub: 'Isolator switch',
            preview: (
                <svg width="28" height="36" viewBox="0 0 28 36" style={{ flexShrink: 0 }}>
                    <circle cx="14" cy="8" r="4" fill="#f8fafc" stroke="#64748b" strokeWidth="1.5" />
                    <circle cx="14" cy="28" r="4" fill="#f8fafc" stroke="#64748b" strokeWidth="1.5" />
                    <line x1="14" y1="8" x2="22" y2="23" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
            ),
        },
        {
            key: 'Gateway', label: 'Gateway / RTU', sub: 'DPM-C530E',
            preview: (
                <svg width="28" height="28" viewBox="0 0 28 28" style={{ flexShrink: 0 }}>
                    <rect x="1" y="1" width="26" height="26" rx="3" fill="#f8fafc" stroke="#1d4ed8" strokeWidth="1.5" />
                    <path d="M7 13 Q14 6 21 13" fill="none" stroke="#1d4ed8" strokeWidth="1.3" strokeLinecap="round" />
                    <path d="M10 17 Q14 12 18 17" fill="none" stroke="#1d4ed8" strokeWidth="1.3" strokeLinecap="round" />
                    <circle cx="14" cy="21" r="2" fill="#1d4ed8" />
                </svg>
            ),
        },
        {
            key: 'BusBar', label: 'Bus Bar', sub: 'Main / Sub bus',
            preview: (
                <svg width="48" height="14" viewBox="0 0 48 14" style={{ flexShrink: 0 }}>
                    <line x1="2" y1="7" x2="46" y2="7" stroke="#e07820" strokeWidth="5" strokeLinecap="round" />
                    <line x1="12" y1="7" x2="12" y2="14" stroke="#64748b" strokeWidth="1.5" />
                    <line x1="36" y1="7" x2="36" y2="14" stroke="#64748b" strokeWidth="1.5" />
                </svg>
            ),
        },
        {
            key: 'Load', label: 'Load / Panel', sub: 'Consumer / sub-panel',
            preview: (
                <svg width="48" height="22" viewBox="0 0 48 22" style={{ flexShrink: 0 }}>
                    <rect x="1" y="1" width="46" height="20" rx="2" fill="white" stroke="#94a3b8" strokeWidth="1" />
                    <text x="24" y="15" textAnchor="middle" fontSize="9" fill="#374151" fontFamily="Arial,sans-serif">Load</text>
                </svg>
            ),
        },
        {
            key: 'TextLabel', label: 'Text Label', sub: 'Free text · double-click to edit',
            preview: (
                <svg width="52" height="22" viewBox="0 0 52 22" style={{ flexShrink: 0 }}>
                    <rect x="1" y="1" width="50" height="20" rx="2" fill="transparent" stroke="#94a3b8" strokeWidth="1" strokeDasharray="3 2" />
                    <text x="26" y="15" textAnchor="middle" fontSize="10" fill="#374151" fontFamily="Arial,sans-serif" fontWeight="600">Aa Text</text>
                </svg>
            ),
        },
    ];

    const Palette = () => (
        <Box sx={{
            width: 260, display: 'flex', flexDirection: 'column', height: '100%',
            borderRight: '1px solid #d1d5db', bgcolor: '#1a1f2e', overflow: 'hidden',
        }}
            onMouseDown={e => e.stopPropagation()}
            onMouseMove={e => e.stopPropagation()}
            onMouseUp={e => e.stopPropagation()}
            onClick={e => e.stopPropagation()}>
            <Box sx={{ p: '10px 12px 8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#475569' }}>
                    SLD Components
                </Typography>
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto', p: 1, '&::-webkit-scrollbar': { width: 3 }, '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.1)', borderRadius: 2 } }}>
                {SLD_PALETTE_ITEMS.map(item => (
                    <Box key={item.key} draggable
                        onDragStart={(e) => { e.dataTransfer.setData('text/plain', item.key); e.dataTransfer.effectAllowed = 'copy'; }}
                        sx={{
                            display: 'flex', alignItems: 'center', gap: 1.5,
                            p: '7px 8px', borderRadius: '5px',
                            border: '1px solid rgba(255,255,255,0.05)',
                            bgcolor: 'rgba(255,255,255,0.02)',
                            cursor: 'grab', mb: 0.75,
                            transition: 'all .15s',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.07)', borderColor: 'rgba(255,255,255,0.12)' },
                        }}>
                        {item.preview}
                        <Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 500, color: '#cbd5e1', lineHeight: 1.2 }}>{item.label}</Typography>
                            <Typography sx={{ fontSize: 10, color: '#475569', mt: '2px' }}>{item.sub}</Typography>
                        </Box>
                    </Box>
                ))}

                <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.06)' }} />
                <Typography sx={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#334155', px: 0.5, mb: 1 }}>
                    Wire Style
                </Typography>

                {/* Color swatches */}
                <Box sx={{ display: 'flex', gap: 0.75, mb: 1, px: 0.5, flexWrap: 'wrap' }}>
                    {['#374151', '#e07820', '#22863a', '#cc2222', '#1d4ed8', '#9333ea'].map(c => (
                        <Box key={c}
                            onClick={() => setLineColor(c)}
                            sx={{
                                width: 20, height: 20, borderRadius: '3px', bgcolor: c,
                                border: lineColor === c ? '2px solid #f59e0b' : '1px solid rgba(255,255,255,0.15)',
                                cursor: 'pointer',
                            }} />
                    ))}
                </Box>

                <Box sx={{ px: 0.5 }}>
                    <Typography sx={{ fontSize: 10, color: '#475569', mb: 0.5 }}>Width: {lineWidth}px</Typography>
                    <Slider value={lineWidth} onChange={(_, v) => setLineWidth(v)} min={1} max={6} step={0.5} size="small"
                        sx={{ color: '#f59e0b' }}
                        onMouseDown={e => e.stopPropagation()} />
                </Box>

                <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.06)' }} />
                <Typography sx={{ fontSize: 10, color: '#334155', lineHeight: 1.8, px: 0.5 }}>
                    <Box component="span" sx={{ color: '#64748b', display: 'block' }}>Select → drag / resize</Box>
                    <Box component="span" sx={{ color: '#64748b', display: 'block' }}>Wire → orthogonal lines</Box>
                    <Box component="span" sx={{ color: '#64748b', display: 'block' }}>Del → remove selected</Box>
                    <Box component="span" sx={{ color: '#64748b', display: 'block' }}>Ctrl+Z/Y → undo/redo</Box>
                </Typography>
            </Box>
        </Box>
    );

    // ==================== PROPERTIES DRAWER ====================
    const PropertiesPanel = () => {
        const selEl = elements.find(e => e.id === selectedId);
        const selLine = freeLines.find(l => l.id === selectedId);

        const getAvailableMeters = () => {
            if (!apiData.length) return [];
            const takenMeters = new Set();
            elements.forEach(el => { if (el.id !== selEl?.id && el.assignedMeter) takenMeters.add(el.assignedMeter); });
            return apiData.filter(m => !takenMeters.has(m.serial_number) || m.serial_number === selEl?.assignedMeter);
        };

        const handleSerialNumberChange = async (event) => {
            const serial = event.target.value;
            if (!serial) {
                if (selEl.assignedMeter) {
                    setAssignedMeters(prev => { const s = { ...prev }; delete s[selEl.assignedMeter]; return s; });
                    updateElement(selEl.id, { label: "0.00", name: "Energy Meter", assignedMeter: null, meterClientId: null });
                    setMeterData(null);
                    showSnackbar("Meter unassigned");
                }
                return;
            }
            let currentApiData = apiData;
            if (currentApiData.length === 0) currentApiData = await fetchMeterData();
            const selectedData = currentApiData.find(item => item.serial_number === serial);
            if (!selectedData) { showSnackbar("Meter data not found", "error"); return; }
            const isAlreadyAssigned = elements.some(el => el.id !== selEl.id && el.assignedMeter === serial);
            if (isAlreadyAssigned) { showSnackbar("This meter is already assigned!", "warning"); return; }
            setAssignedMeters(prev => ({ ...prev, [serial]: selEl.id }));
            setMeterData(selectedData);
            const powerKw = selectedData.data?.[0]?.total_active_power_kw || 0;
            updateElement(selEl.id, {
                label: `${powerKw}`,
                name: selectedData.description || selectedData.usage || "Meter",
                assignedMeter: serial,
                meterClientId: meterDataSourceClientId,
            });
            showSnackbar(`Meter assigned`);
        };

        const handleMeterDataSourceChange = async (e) => {
            const newClientId = parseInt(e.target.value);
            setMeterDataSourceClientId(newClientId);
            meterDataSourceClientIdRef.current = newClientId;
            localStorage.setItem("meter_data_source_client_id", String(newClientId));
            setApiData([]); setMeterData(null);
            const updatedElements = elements.map(el =>
                el.type === 'Meter' && el.assignedMeter
                    ? { ...el, label: "0.00", name: "Energy Meter", assignedMeter: null, meterClientId: null }
                    : el
            );
            setElements(updatedElements);
            setAssignedMeters({});
            const meters = await fetchMeterData(newClientId);
            showSnackbar(meters.length > 0 ? `Loaded ${meters.length} meters` : "No meters found", meters.length > 0 ? "success" : "warning");
        };

        const inputSx = {
            '& .MuiOutlinedInput-root': {
                fontSize: 12, bgcolor: 'rgba(0,0,0,0.2)',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
                '&.Mui-focused fieldset': { borderColor: 'rgba(245,158,11,0.5)' },
            },
            '& .MuiInputLabel-root': { fontSize: 12, color: '#64748b' },
            '& .MuiInputBase-input': { color: '#cbd5e1' },
            '& .MuiSvgIcon-root': { color: '#64748b' },
        };

        const sectionLabel = (text) => (
            <Typography sx={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#475569', mb: 0.75, mt: 1.5 }}>
                {text}
            </Typography>
        );

        return (
            <Drawer anchor="right" open={showProperties} onClose={() => setShowProperties(false)}
                hideBackdrop variant="persistent"
                sx={{
                    '& .MuiDrawer-paper': {
                        width: 280, p: 0, bgcolor: '#1a1f2e',
                        borderLeft: '1px solid rgba(255,255,255,0.06)',
                        boxShadow: '-4px 0 20px rgba(0,0,0,0.3)',
                    }
                }}>

                <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#94a3b8' }}>Properties</Typography>
                    <IconButton size="small" onClick={() => setShowProperties(false)} sx={{ color: '#475569', '&:hover': { color: '#94a3b8' } }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, '&::-webkit-scrollbar': { width: 3 }, '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.1)' } }}>
                    {selEl && (
                        <>
                            {/* Element header */}
                            <Box sx={{ p: 1.5, bgcolor: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 1.5, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#f59e0b', flexShrink: 0 }} />
                                <Box>
                                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0' }}>{selEl.name}</Typography>
                                    <Typography sx={{ fontSize: 10, color: '#f59e0b', mt: '1px' }}>{COMPONENT_TYPES[selEl.type]?.name || selEl.type}</Typography>
                                </Box>
                            </Box>

                            {sectionLabel('Label')}
                            <TextField label="Label" value={selEl.label}
                                onChange={e => updateElement(selEl.id, { label: e.target.value })}
                                fullWidth size="small" sx={{ ...inputSx, mb: 1 }} />

                            {/* Bus bar color picker */}
                            {selEl.type === 'BusBar' && (
                                <>
                                    {sectionLabel('Bus Color')}
                                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                        {['#e07820', '#22863a', '#cc2222', '#1d4ed8', '#374151', '#9333ea'].map(c => (
                                            <Box key={c}
                                                onClick={() => updateElement(selEl.id, { color: c })}
                                                sx={{
                                                    width: 24, height: 24, borderRadius: '4px', bgcolor: c,
                                                    border: selEl.color === c ? '2px solid #f59e0b' : '1px solid rgba(255,255,255,0.15)',
                                                    cursor: 'pointer',
                                                }} />
                                        ))}
                                    </Box>
                                </>
                            )}

                            {/* Text Label formatting */}
                            {selEl.type === 'TextLabel' && (
                                <>
                                    <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.06)' }} />
                                    {sectionLabel('Text Formatting')}

                                    {/* Font size */}
                                    <Box sx={{ mb: 1 }}>
                                        <Typography sx={{ fontSize: 10, color: '#475569', mb: 0.5 }}>
                                            Font size: {selEl.fontSize || 13}px
                                        </Typography>
                                        <Slider
                                            value={selEl.fontSize || 13}
                                            min={8} max={48} step={1} size="small"
                                            onChange={(_, v) => updateElement(selEl.id, { fontSize: v })}
                                            sx={{ color: '#f59e0b' }}
                                        />
                                    </Box>

                                    {/* Bold / Italic toggles */}
                                    <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                                        <Box onClick={() => updateElement(selEl.id, { bold: !selEl.bold })}
                                            sx={{
                                                flex: 1, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                borderRadius: '4px', border: '1px solid',
                                                borderColor: selEl.bold ? 'rgba(245,158,11,0.5)' : 'rgba(255,255,255,0.08)',
                                                bgcolor: selEl.bold ? 'rgba(245,158,11,0.1)' : 'rgba(255,255,255,0.02)',
                                                cursor: 'pointer', fontSize: 13, fontWeight: 700,
                                                color: selEl.bold ? '#f59e0b' : '#475569',
                                                transition: 'all .15s',
                                            }}>B</Box>
                                        <Box onClick={() => updateElement(selEl.id, { italic: !selEl.italic })}
                                            sx={{
                                                flex: 1, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                borderRadius: '4px', border: '1px solid',
                                                borderColor: selEl.italic ? 'rgba(245,158,11,0.5)' : 'rgba(255,255,255,0.08)',
                                                bgcolor: selEl.italic ? 'rgba(245,158,11,0.1)' : 'rgba(255,255,255,0.02)',
                                                cursor: 'pointer', fontSize: 13, fontStyle: 'italic',
                                                color: selEl.italic ? '#f59e0b' : '#475569',
                                                transition: 'all .15s',
                                            }}>I</Box>
                                        <Box onClick={() => updateElement(selEl.id, { showBorder: !selEl.showBorder })}
                                            sx={{
                                                flex: 1, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                borderRadius: '4px', border: '1px solid',
                                                borderColor: selEl.showBorder ? 'rgba(245,158,11,0.5)' : 'rgba(255,255,255,0.08)',
                                                bgcolor: selEl.showBorder ? 'rgba(245,158,11,0.1)' : 'rgba(255,255,255,0.02)',
                                                cursor: 'pointer', fontSize: 10,
                                                color: selEl.showBorder ? '#f59e0b' : '#475569',
                                                transition: 'all .15s',
                                            }}>Border</Box>
                                    </Box>

                                    {/* Text color */}
                                    {sectionLabel('Text Color')}
                                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
                                        {['#1f2937', '#374151', '#cc2222', '#1d4ed8', '#22863a', '#e07820', '#9333ea', '#ffffff'].map(c => (
                                            <Box key={c}
                                                onClick={() => updateElement(selEl.id, { textColor: c })}
                                                sx={{
                                                    width: 22, height: 22, borderRadius: '3px', bgcolor: c,
                                                    border: (selEl.textColor || '#1f2937') === c ? '2px solid #f59e0b' : '1px solid rgba(255,255,255,0.15)',
                                                    cursor: 'pointer',
                                                }} />
                                        ))}
                                    </Box>

                                    <Button size="small" variant="outlined" fullWidth
                                        onClick={() => {
                                            setEditingTextId(selEl.id);
                                            setEditingTextValue(selEl.label);
                                        }}
                                        sx={{ fontSize: 11, color: '#f59e0b', borderColor: 'rgba(245,158,11,0.35)', '&:hover': { borderColor: '#f59e0b', bgcolor: 'rgba(245,158,11,0.06)' }, textTransform: 'none', mb: 1 }}>
                                        ✏️ Edit Text (or double-click)
                                    </Button>
                                </>
                            )}

                            {/* Meter assignment */}
                            {selEl?.type === 'Meter' && (
                                <>
                                    <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.06)' }} />
                                    {sectionLabel('Meter Data Source')}

                                    {clientList.length > 0 && (
                                        <TextField select fullWidth size="small" label="Client"
                                            value={meterDataSourceClientId || ''} onChange={handleMeterDataSourceChange}
                                            disabled={loadingClients} sx={{ ...inputSx, mb: 1 }}>
                                            {clientList.map((client) => (
                                                <MenuItem key={client.client_id} value={client.client_id}>
                                                    <Typography sx={{ fontSize: 12 }}>{client.company_name}</Typography>
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    )}

                                    {sectionLabel('Assign Meter')}
                                    {loadingData ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, gap: 1 }}>
                                            <CircularProgress size={18} sx={{ color: '#f59e0b' }} />
                                            <Typography sx={{ fontSize: 11, color: '#64748b' }}>Loading meters...</Typography>
                                        </Box>
                                    ) : (
                                        <>
                                            <TextField select fullWidth size="small" label="Serial number"
                                                value={selEl.assignedMeter || ''} onChange={handleSerialNumberChange}
                                                disabled={apiData.length === 0} sx={{ ...inputSx, mb: 0.5 }}>
                                                <MenuItem value=""><em style={{ color: '#475569', fontSize: 12 }}>None</em></MenuItem>
                                                {getAvailableMeters().map((item) => (
                                                    <MenuItem key={item.serial_number} value={item.serial_number}>
                                                        <Box>
                                                            <Typography sx={{ fontSize: 12, fontWeight: 500 }}>{item.description || item.usage || item.serial_number}</Typography>
                                                            <Typography sx={{ fontSize: 10, color: '#64748b' }}>SN: {item.serial_number}</Typography>
                                                        </Box>
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                            {selEl.assignedMeter && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
                                                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#22c55e' }} />
                                                    <Typography sx={{ fontSize: 10, color: '#22c55e' }}>Live · {selEl.assignedMeter}</Typography>
                                                </Box>
                                            )}
                                        </>
                                    )}

                                    {/* Live readings */}
                                    {meterData?.data?.length > 0 && selEl.assignedMeter === meterData.serial_number && (
                                        <>
                                            {sectionLabel('Live Reading')}
                                            <Box sx={{ bgcolor: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 1.5, overflow: 'hidden' }}>
                                                {[
                                                    { k: 'Active Power', v: `${meterData.data[0].total_active_power_kw} kW`, hi: true },
                                                    { k: 'Current', v: `${meterData.data[0].total_apparent_power_kva} kVA` },
                                                    { k: 'Power Factor', v: `${meterData.data[0].total_pf}` },
                                                ].map((row, i, arr) => (
                                                    <Box key={row.k} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1.5, py: 0.75, borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                                                        <Typography sx={{ fontSize: 10, color: '#475569' }}>{row.k}</Typography>
                                                        <Typography sx={{ fontSize: row.hi ? 14 : 11, fontWeight: row.hi ? 600 : 400, color: row.hi ? '#f59e0b' : '#e2e8f0', fontFamily: 'monospace' }}>{row.v}</Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </>
                                    )}
                                </>
                            )}

                            <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.06)' }} />
                            {sectionLabel('Size')}
                            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                                <TextField label="W" type="number" size="small" value={selEl.width}
                                    onChange={e => updateElement(selEl.id, { width: Math.min(selEl.maxWidth, Math.max(selEl.minWidth, +e.target.value || selEl.width)) })}
                                    sx={inputSx} />
                                <TextField label="H" type="number" size="small" value={selEl.height}
                                    onChange={e => updateElement(selEl.id, { height: Math.min(selEl.maxHeight, Math.max(selEl.minHeight, +e.target.value || selEl.height)) })}
                                    sx={inputSx} />
                            </Stack>

                            <Stack direction="row" spacing={1}>
                                <Button size="small" startIcon={<CopyIcon />} onClick={() => duplicateElement(selEl.id)}
                                    sx={{ flex: 1, fontSize: 11, color: '#64748b', borderColor: 'rgba(255,255,255,0.08)', '&:hover': { color: '#94a3b8', borderColor: 'rgba(255,255,255,0.18)' } }}
                                    variant="outlined">Clone</Button>
                                <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => deleteElement(selEl.id)}
                                    sx={{ flex: 1, fontSize: 11 }} variant="outlined">Delete</Button>
                            </Stack>
                        </>
                    )}

                    {selLine && (
                        <>
                            {sectionLabel('Wire')}
                            <Box sx={{ mb: 1.5 }}>
                                <Typography sx={{ fontSize: 10, color: '#475569', mb: 0.75 }}>Color</Typography>
                                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                    {['#374151', '#e07820', '#22863a', '#cc2222', '#1d4ed8', '#9333ea'].map(c => (
                                        <Box key={c}
                                            onClick={() => setFreeLines(freeLines.map(l => l.id === selLine.id ? { ...l, color: c } : l))}
                                            sx={{ width: 22, height: 22, borderRadius: '3px', bgcolor: c, border: selLine.color === c ? '2px solid #f59e0b' : '1px solid rgba(255,255,255,0.15)', cursor: 'pointer' }} />
                                    ))}
                                </Box>
                            </Box>
                            <Box sx={{ mb: 1.5 }}>
                                <Typography sx={{ fontSize: 10, color: '#475569', mb: 0.5 }}>Width: {selLine.width}px</Typography>
                                <Slider value={selLine.width} min={1} max={6} step={0.5} size="small"
                                    onChange={(_, v) => setFreeLines(freeLines.map(l => l.id === selLine.id ? { ...l, width: v } : l))}
                                    sx={{ color: '#f59e0b' }} />
                            </Box>
                            <TextField select label="Style" value={selLine.style} size="small" fullWidth sx={{ ...inputSx, mb: 1.5 }}
                                onChange={e => setFreeLines(freeLines.map(l => l.id === selLine.id ? { ...l, style: e.target.value } : l))}>
                                <MenuItem value="solid">Solid</MenuItem>
                                <MenuItem value="dashed">Dashed</MenuItem>
                            </TextField>
                            <Button fullWidth variant="outlined" color="error" size="small" startIcon={<DeleteIcon />}
                                onClick={() => deleteLine(selLine.id)}>Delete Wire</Button>
                        </>
                    )}
                </Box>
            </Drawer>
        );
    };

    // ==================== MAIN RENDER ====================
    const cursorStyle = panning ? 'grabbing' : mode === 'pan' ? 'grab' : mode === 'line' ? 'crosshair' : 'default';

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
            <input type="file" id="import-file" accept=".json" style={{ display: 'none' }} />

            {/* ===== TOOLBAR ===== */}
            <Box sx={{
                height: 46, px: 1.5, display: 'flex', alignItems: 'center', gap: 0.75,
                bgcolor: '#1e2330', borderBottom: '1px solid #0d0f16', flexShrink: 0,
            }}>
                {/* Brand */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mr: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#e2e8f0', letterSpacing: '0.05em' }}>
                        SLD EDITOR
                    </Typography>
                </Box>

                <Chip label={`${elements.length} nodes`} size="small"
                    sx={{ height: 22, fontSize: 11, bgcolor: 'rgba(255,255,255,0.05)', color: '#64748b', border: '1px solid rgba(255,255,255,0.08)' }} />
                <Chip label={`${freeLines.length} wires`} size="small"
                    sx={{ height: 22, fontSize: 11, bgcolor: 'rgba(255,255,255,0.05)', color: '#64748b', border: '1px solid rgba(255,255,255,0.08)' }} />
                {drawingLine && (
                    <Chip label="Drawing wire… release to finish" size="small" color="warning"
                        sx={{ height: 22, fontSize: 11 }} />
                )}

                <Box sx={{ flex: 1 }} />

                {/* Mode group */}
                <ToggleButtonGroup value={mode} exclusive size="small"
                    onChange={(_, v) => { if (v) { setMode(v); setDrawingLine(null); } }}
                    sx={{
                        bgcolor: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '6px', p: '2px', gap: '2px',
                        '& .MuiToggleButton-root': {
                            border: 'none', borderRadius: '4px !important', px: 1.5, py: 0.5,
                            fontSize: 11, color: '#64748b', textTransform: 'none', fontWeight: 500,
                            '&.Mui-selected': { bgcolor: 'rgba(245,158,11,0.15)', color: '#f59e0b' },
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.05)', color: '#e2e8f0' },
                        },
                    }}>
                    <ToggleButton value="select"><Tooltip title="Select / Move"><SelectAllIcon sx={{ fontSize: 16, mr: 0.5 }} />Select</Tooltip></ToggleButton>
                    <ToggleButton value="line"><Tooltip title="Draw Wire (orthogonal)">
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ marginRight: 4 }}><path d="M2 14L14 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
                        Wire
                    </Tooltip></ToggleButton>
                    <ToggleButton value="pan"><Tooltip title="Pan"><PanToolIcon sx={{ fontSize: 16, mr: 0.5 }} />Pan</Tooltip></ToggleButton>
                </ToggleButtonGroup>

                <Box sx={{ width: 1, height: 22, bgcolor: 'rgba(255,255,255,0.08)', mx: 0.5 }} />

                <Tooltip title="Undo (Ctrl+Z)">
                    <span><IconButton onClick={undo} disabled={historyIndex <= 0} size="small" sx={{ color: '#64748b', '&:hover': { color: '#e2e8f0' }, '&.Mui-disabled': { color: '#2d3748' } }}>
                        <UndoIcon sx={{ fontSize: 18 }} />
                    </IconButton></span>
                </Tooltip>
                <Tooltip title="Redo (Ctrl+Y)">
                    <span><IconButton onClick={redo} disabled={historyIndex >= history.length - 1} size="small" sx={{ color: '#64748b', '&:hover': { color: '#e2e8f0' }, '&.Mui-disabled': { color: '#2d3748' } }}>
                        <RedoIcon sx={{ fontSize: 18 }} />
                    </IconButton></span>
                </Tooltip>

                <Box sx={{ width: 1, height: 22, bgcolor: 'rgba(255,255,255,0.08)', mx: 0.5 }} />

                <Tooltip title="Zoom Out"><IconButton onClick={handleZoomOut} size="small" sx={{ color: '#64748b', '&:hover': { color: '#e2e8f0' } }}><ZoomOutIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                <Typography sx={{ fontSize: 11, color: '#64748b', fontFamily: 'monospace', minWidth: 40, textAlign: 'center' }}>{Math.round(zoom * 100)}%</Typography>
                <Tooltip title="Zoom In"><IconButton onClick={handleZoomIn} size="small" sx={{ color: '#64748b', '&:hover': { color: '#e2e8f0' } }}><ZoomInIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                <Tooltip title="Reset View"><IconButton onClick={handleZoomReset} size="small" sx={{ color: '#64748b', '&:hover': { color: '#e2e8f0' } }}><RestartAltIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>

                <Box sx={{ width: 1, height: 22, bgcolor: 'rgba(255,255,255,0.08)', mx: 0.5 }} />

                <Tooltip title={showGrid ? 'Hide Grid' : 'Show Grid'}>
                    <IconButton onClick={() => setShowGrid(g => !g)} size="small" sx={{ color: showGrid ? '#f59e0b' : '#64748b', '&:hover': { color: '#e2e8f0' } }}>
                        {showGrid ? <GridOnIcon sx={{ fontSize: 18 }} /> : <GridOffIcon sx={{ fontSize: 18 }} />}
                    </IconButton>
                </Tooltip>

                <Tooltip title="Clear All">
                    <IconButton onClick={clearCanvas} size="small" sx={{ color: '#64748b', '&:hover': { color: '#ef4444' } }}>
                        <DeleteForeverIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>

                <Box sx={{ width: 1, height: 22, bgcolor: 'rgba(255,255,255,0.08)', mx: 0.5 }} />

                <Button onClick={saveToDB} size="small" variant="contained"
                    sx={{ bgcolor: '#f59e0b', color: '#1a1a1a', fontWeight: 700, fontSize: 11, px: 2, py: 0.5, minHeight: 28, '&:hover': { bgcolor: '#d97706' }, textTransform: 'none', mr: 0.5 }}
                    startIcon={<SaveIcon sx={{ fontSize: 15 }} />}>
                    Save
                </Button>

                <Tooltip title="Exit">
                    <IconButton onClick={goBackToGallery} size="small" sx={{ color: '#64748b', '&:hover': { color: '#e2e8f0' } }}>
                        <ClearIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* ===== BODY ===== */}
            <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                <Palette />

                {/* ===== CANVAS ===== */}
                <Box ref={canvasRef}
                    sx={{
                        flex: 1, position: 'relative', overflow: 'hidden', cursor: cursorStyle,
                        bgcolor: '#f4f5f7',
                        backgroundImage: showGrid
                            ? 'linear-gradient(rgba(0,0,0,0.06) 1px,transparent 1px),linear-gradient(90deg,rgba(0,0,0,0.06) 1px,transparent 1px)'
                            : 'none',
                        backgroundSize: `${30 * zoom}px ${30 * zoom}px`,
                        backgroundPosition: `${panOffset.x}px ${panOffset.y}px`,
                    }}
                    onMouseDown={handleCanvasMouseDown}
                    onDrop={handleDrop}
                    onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}>

                    <svg width="100%" height="100%"
                        style={{ position: 'absolute', top: 0, left: 0, overflow: 'visible' }}>
                        <g transform={`translate(${panOffset.x},${panOffset.y}) scale(${zoom})`}>
                            {renderLines()}
                            {renderElements()}

                            {/* Inline text editor overlay for TextLabel double-click */}
                            {editingTextId && (() => {
                                const el = elements.find(e => e.id === editingTextId);
                                if (!el) return null;
                                const sx = el.x * zoom + panOffset.x;
                                const sy = el.y * zoom + panOffset.y;
                                const sw = el.width * zoom;
                                const sh = el.height * zoom;
                                return (
                                    <foreignObject x={sx} y={sy} width={sw} height={Math.max(sh, 60)}
                                        style={{ overflow: 'visible' }}>
                                        <textarea
                                            xmlns="http://www.w3.org/1999/xhtml"
                                            autoFocus
                                            value={editingTextValue}
                                            onChange={e => setEditingTextValue(e.target.value)}
                                            onBlur={() => {
                                                updateElement(editingTextId, { label: editingTextValue });
                                                setEditingTextId(null);
                                            }}
                                            onKeyDown={e => {
                                                if (e.key === 'Escape') {
                                                    setEditingTextId(null);
                                                }
                                                e.stopPropagation();
                                            }}
                                            style={{
                                                width: '100%',
                                                minHeight: `${Math.max(sh, 60)}px`,
                                                fontSize: `${(el.fontSize || 13) * zoom}px`,
                                                fontWeight: el.bold ? '700' : '400',
                                                fontStyle: el.italic ? 'italic' : 'normal',
                                                color: el.textColor || '#1f2937',
                                                fontFamily: "'Segoe UI', Arial, sans-serif",
                                                background: 'rgba(255,255,255,0.97)',
                                                border: '2px solid #f59e0b',
                                                borderRadius: '3px',
                                                padding: '4px 6px',
                                                outline: 'none',
                                                resize: 'none',
                                                lineHeight: 1.4,
                                                boxSizing: 'border-box',
                                                boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
                                            }}
                                        />
                                    </foreignObject>
                                );
                            })()}

                            {/* Snap alignment guides */}
                            {snapGuides.v.map((g, i) => (
                                <line key={`vg-${i}`} x1={g.x} y1={g.y1} x2={g.x} y2={g.y2}
                                    stroke="#f59e0b" strokeWidth={0.75} strokeDasharray="4 3" opacity={0.7}
                                    style={{ pointerEvents: 'none' }} />
                            ))}
                            {snapGuides.h.map((g, i) => (
                                <line key={`hg-${i}`} x1={g.x1} y1={g.y} x2={g.x2} y2={g.y}
                                    stroke="#f59e0b" strokeWidth={0.75} strokeDasharray="4 3" opacity={0.7}
                                    style={{ pointerEvents: 'none' }} />
                            ))}

                            {/* Drawing wire preview */}
                            {drawingLine && (
                                <line x1={drawingLine.startX} y1={drawingLine.startY}
                                    x2={drawingLine.currentX} y2={drawingLine.currentY}
                                    stroke={lineColor} strokeWidth={lineWidth} strokeDasharray="6 4" opacity={0.75}
                                    style={{ pointerEvents: 'none' }} />
                            )}

                            {/* Anchor snap indicator */}
                            {snappedEnd && drawingLine && (
                                <circle cx={snappedEnd.x} cy={snappedEnd.y} r={8}
                                    fill="none" stroke="#f59e0b" strokeWidth={2} opacity={0.9}
                                    style={{ pointerEvents: 'none' }} />
                            )}
                        </g>
                    </svg>
                </Box>
            </Box>

            <PropertiesPanel />

            {/* ===== STATUS BAR ===== */}
            <Box sx={{ height: 24, bgcolor: '#1e2330', borderTop: '1px solid #0d0f16', display: 'flex', alignItems: 'center', px: 1.5, gap: 2, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#22c55e', boxShadow: '0 0 4px #22c55e' }} />
                    <Typography sx={{ fontSize: 10, color: '#22c55e', fontFamily: 'monospace' }}>Connected</Typography>
                </Box>
                <Typography sx={{ fontSize: 10, color: '#334155', fontFamily: 'monospace' }}>
                    {elements.length} nodes · {freeLines.length} wires
                </Typography>
                <Typography sx={{ fontSize: 10, color: '#334155', fontFamily: 'monospace' }}>
                    {Math.round(zoom * 100)}%
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Typography sx={{ fontSize: 10, color: '#334155', fontFamily: 'monospace' }}>
                    {diagramName}
                </Typography>
            </Box>

            <Snackbar open={snackbar.open} autoHideDuration={2500}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snackbar.severity} variant="filled" sx={{ fontSize: 13 }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default DiagramEditor;